import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap';
import './AIOffset.scss';
import Header from './components/Header/Header';
import ChatHistory from './components/ChatHistory/ChatHistory';
import WelcomeSection from './components/WelcomeSection/WelcomeSection';
import { AiChatType } from 'types';
import { ChatMessagesHistory } from './components/ChatMessagesHistory';
import ImportAndUpload from './components/ImportAndUpload/ImportAndUpload';
import { useCreateNewChat } from 'hooks/ai-chat/useCreateNewChat';
import { Loading } from 'components/Loading';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useAppStore } from 'hooks/useAppStore';
import { useLocation } from 'react-router-dom';

interface OffcanvasMenuProps {
  className?: string;
}

const AIOffset: React.FC<OffcanvasMenuProps> = observer(({ className }) => {
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<AiChatType>(null);
  const previousViewMode = useRef<AiChatType>(null);
  const [expanded, setExpanded] = useState<boolean>(false);
  const { mutateAsync: createChat, isLoading: isCreateChatLoading } = useCreateNewChat();
  const { uiStore } = useAppStore();
  const location = useLocation();

  useEffect(() => {
    if (viewMode !== previousViewMode.current) {
      previousViewMode.current = viewMode;
    }
  }, [viewMode, previousViewMode]);

  const { isAIOffsetOpen } = uiStore;

  const handleChatSelect = (chatId: string) => {
    setSelectedChatId(chatId);
    setViewMode(AiChatType.BRAINSTORM);
  };

  const createNewChat = async (mode: AiChatType) => {
    const newChat = await createChat({ chatType: mode });
    setSelectedChatId(newChat.id);
    setViewMode(mode);
  };

  const mainLayout = useMemo(() => {
    switch (viewMode) {
      case null:
        return <WelcomeSection changeViewMode={mode => createNewChat(mode)} />;
      case AiChatType.IMPORT:
        return (
          <ImportAndUpload
            chatId={selectedChatId}
            analyzeSuccess={() => {
              setViewMode(AiChatType.BRAINSTORM);
            }}
            onBack={() => {
              setViewMode(null);
              setSelectedChatId(null);
            }}
          />
        );
      case AiChatType.HISTORY:
        return (
          <ChatHistory
            isWelcome={!viewMode}
            onChatSelect={handleChatSelect}
            changeViewMode={mode => (mode ? createNewChat(mode) : setViewMode(null))}
            selectedChatId={selectedChatId}
            setSelectedChatId={setSelectedChatId}
          />
        );
      default:
        return (
          <ChatMessagesHistory
            chatId={selectedChatId}
            previousViewMode={previousViewMode.current}
          />
        );
    }
  }, [viewMode, selectedChatId, isCreateChatLoading]);

  useEffect(() => {
    setExpanded(false);
  }, [location]);

  useEffect(() => {
    return () => {
      setViewMode(null);
      previousViewMode.current = null;
      setSelectedChatId(null);
    };
  }, []);

  return (
    <Offcanvas
      isOpen={isAIOffsetOpen}
      toggle={() => uiStore?.toggleAIOffset()}
      direction="end"
      backdrop={true}
      zIndex={1002}
      className={classNames(className, { expanded: expanded })}
    >
      <OffcanvasHeader>
        <Header
          isWelcome={!viewMode}
          onClose={() => uiStore?.toggleAIOffset()}
          expanded={expanded}
          toggleExpand={setExpanded}
          displayWelcomeScreen={() => {
            setViewMode(null);
            setSelectedChatId(null);
            uiStore.setAIScrollToBottom(false);
          }}
          changeViewMode={setViewMode}
          viewMode={viewMode}
          previousViewMode={previousViewMode.current}
        />
      </OffcanvasHeader>
      <OffcanvasBody>
        <div className="d-flex flex-column flex-column-reverse flex-lg-row h-100">
          <div className="bg-white h-100 w-100">{isCreateChatLoading ? <Loading /> : mainLayout}</div>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
});

export default AIOffset;
